import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from './Modal';
import { Text } from '../text';
import { InfoListItem } from '../InfoListItem';
import { TopCardWrapper } from '../../auth/cabinet/payments/TopCardWrapper';
import { accountRoutes } from '../../../constants/routes';
import { PlanPrice } from '../PlanPrice';
import { usePaymentPlans } from '../../../hooks/common/usePaymentPlans';
import { useBuyTop } from '../../../hooks/auth/cabinet/payments/useBuyTop';
import { useBuyBundle } from '../../../hooks/auth/cabinet/payments/useBuyBundle';
import { useHighlightAdvert } from '../../../hooks/auth/cabinet/payments/useHighlightAdvert';
import { trackEvent } from '../../../utils/common/analytics';
import {
    selectPostCreateUpsell,
    closePostCreateUpsell,
} from '../../../redux/reducers/common/modals/postCreateUpsell';

const TOPS_ROUTE_BY_TABLE = {
    sell_car: accountRoutes.SELL_TOPS_ID,
    sell_bike: accountRoutes.SELL_TOPS_ID,
    sell_commercial: accountRoutes.SELL_TOPS_ID,
    rent: accountRoutes.RENT_TOPS_ID,
};

const bundleSaving = (plans) =>
    (Number(plans.top?.premium) || 0) +
    (Number(plans.highlight?.highlight_month) || 0) -
    (Number(plans.bundle?.bundle_premium_highlight) || 0);

const TIER_ROWS = [
    {
        level: 'bundle',
        titleKey: 'upsellTierBundleTitle',
        days: 30,
        highlighted: true,
        price: (plans) => plans.bundle.bundle_premium_highlight,
        features: (plans) => [
            ['upsellFeatBundleTop'],
            ['upsellFeatBundleHighlight'],
            bundleSaving(plans) > 0 && ['upsellFeatBundleSave', { gel: bundleSaving(plans) }],
        ],
    },
    {
        level: 'premium',
        titleKey: 'premium',
        days: 30,
        price: (plans) => plans.top.premium,
        features: () => [['upsellFeatTopFirst'], ['upsellFeatBoost'], ['upsellFeatFastSell']],
    },
    {
        level: 'one',
        titleKey: 'top-1',
        days: 14,
        price: (plans) => plans.top.top1,
        features: () => [['upsellFeatTopOne'], ['upsellFeatBoost'], ['upsellFeatFastSell']],
    },
    {
        level: 'two',
        titleKey: 'top-2',
        days: 7,
        price: (plans) => plans.top.top2,
        features: () => [['upsellFeatTopTwo'], ['upsellFeatBoost']],
    },
    {
        level: 'highlight',
        titleKey: 'highlightModalTitle',
        days: 7,
        sellOnly: true,
        price: (plans) => plans.highlight.highlight_week,
        features: () => [['upsellFeatHighlight'], ['upsellFeatHighlightPlace']],
    },
];

export const PostCreateUpsellModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const plans = usePaymentPlans();
    const { isOpen, item, table, advertId, mode } = useSelector(
        selectPostCreateUpsell,
    );

    const routeTemplate = TOPS_ROUTE_BY_TABLE[table];
    const canRender = isOpen && advertId && routeTemplate;

    const { buyTopPremium, buyTopOne, buyTopTwo } = useBuyTop({
        item: item || { id: advertId },
        table,
        id: advertId,
    });
    const { buyBundlePremiumHighlight } = useBuyBundle({
        item: item || { id: advertId },
        table,
        id: advertId,
    });
    const { highlightWeek } = useHighlightAdvert({
        item: item || { id: advertId },
        table,
        id: advertId,
    });

    const handleClose = () => dispatch(closePostCreateUpsell());

    if (!canRender) return null;

    const handlers = {
        bundle: buyBundlePremiumHighlight,
        premium: buyTopPremium,
        one: buyTopOne,
        two: buyTopTwo,
        highlight: highlightWeek,
    };

    const isSellTable = String(table || '').startsWith('sell_');
    const rows = TIER_ROWS.filter((row) => !row.sellOnly || isSellTable);

    const handleTierClick = (row) => {
        trackEvent('upsell_tier_click', {
            event_category: 'monetization',
            tier: row.level,
            price_gel: row.price(plans),
            table,
            mode,
        });
        handleClose();
        const handler = handlers[row.level];
        if (handler) handler();
    };

    const isEdit = mode === 'edit';
    const titleKey = isEdit ? 'postEditUpsellTitle' : 'postCreateUpsellTitle';
    const bodyKey = isEdit ? 'postEditUpsellBody' : 'postCreateUpsellBody';

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel={t(titleKey)}
            withContinue
            text="exit"
            className="btn btn-main"
        >
            <Text>{t(bodyKey)}</Text>
            {rows.map((row) => (
                <TopCardWrapper
                    key={row.level}
                    topTitle={`${t(row.titleKey)}${row.days ? ` (${row.days} ${t('daysShort')})` : ''}`}
                    priceLabel={<PlanPrice gel={row.price(plans)} />}
                    onPriceClick={() => handleTierClick(row)}
                    highlighted={row.highlighted}
                    stacked
                >
                    {row
                        .features(plans)
                        .filter(Boolean)
                        .map(([key, params]) => (
                            <InfoListItem key={key} text={t(key, params)} />
                        ))}
                </TopCardWrapper>
            ))}
        </Modal>
    );
};
